import {
  arrayUnion,
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAt,
  updateDoc,
  setDoc,
  where,
} from 'firebase/firestore';

import { db, firestorePathId } from './firebase';

const toMillis = (value) => {
  if (value?.toMillis) return value.toMillis();
  if (value?.toDate) return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCentralItem = (snapshot) => {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    storage: 'notificationCenter',
    eventId: data.eventId || snapshot.id,
    eventType: data.eventType || 'atualizacao',
    title: data.title || data.titulo || 'Atualização do SISFOL',
    message: data.message || data.mensagem || '',
    link: data.link || '/dashboard',
    entityType: data.entityType || '',
    entityId: data.entityId || '',
    metadata: data.metadata || {},
    read: data.read === true,
    createdAt: data.createdAt || data.updatedAt || null,
    createdAtMs: toMillis(data.createdAt || data.updatedAt),
  };
};

const normalizeLegacyItem = (snapshot) => {
  const data = snapshot.data() || {};
  const requerimentoId = String(data.requerimentoId || '').trim();
  const roomId = String(data.roomId || '').trim();
  const link = data.link
    || (roomId ? `/ajuda?roomId=${encodeURIComponent(roomId)}` : '')
    || (requerimentoId ? `/requerimentos?requerimentoId=${encodeURIComponent(requerimentoId)}` : '')
    || '/notificacoes';
  return {
    id: snapshot.id,
    storage: 'legacyNotification',
    eventId: data.eventId || `notification:${snapshot.id}`,
    eventType: data.eventType || data.tipo || data.type || 'notificacao_legada',
    title: data.title || data.titulo || 'Atualização do SISFOL',
    message: data.message || data.mensagem || '',
    link,
    entityType: requerimentoId ? 'requerimento' : (roomId ? 'sisconnect' : 'notification'),
    entityId: requerimentoId || roomId || snapshot.id,
    metadata: data.metadata || {},
    read: data.read === true || data.lida === true,
    createdAt: data.createdAt || data.data || data.updatedAt || null,
    createdAtMs: toMillis(data.createdAt || data.data || data.updatedAt),
  };
};

export const normalizeAvisoForNotificationCenter = (aviso, read = false) => {
  const id = String(aviso?.id || '').trim();
  return {
    id,
    storage: 'aviso',
    eventId: `aviso:${id}`,
    eventType: 'aviso_oficial',
    title: aviso?.titulo || 'Aviso oficial',
    message: aviso?.conteudo || aviso?.mensagem || '',
    link: `/avisos?avisoId=${encodeURIComponent(id)}`,
    entityType: 'aviso',
    entityId: id,
    metadata: {tipoDestino: aviso?.tipoDestino || 'todos'},
    read: Boolean(read || aviso?.centralReadByDefault === true),
    createdAt: aviso?.dataCriacao || null,
    createdAtMs: toMillis(aviso?.dataCriacao),
  };
};

const mergeNotifications = (central = [], legacy = []) => {
  const byEvent = new Map();
  [...central, ...legacy].forEach((item) => {
    const key = String(item.eventId || `${item.storage}:${item.id}`);
    const current = byEvent.get(key);
    if (!current || item.createdAtMs > current.createdAtMs || item.storage === 'notificationCenter') {
      byEvent.set(key, item);
    }
  });
  return [...byEvent.values()].sort((a, b) => b.createdAtMs - a.createdAtMs);
};

const CENTRAL_LIVE_PAGE_SIZE = 100;
const CENTRAL_HISTORY_PAGE_SIZE = 50;

export const listenServidorNotificationCenter = ({ uid, onChange, onError }) => {
  const safeUid = String(uid || '').trim();
  if (!safeUid) return () => {};

  const centralRef = collection(db, `artifacts/${firestorePathId}/users/${safeUid}/notificationCenter`);
  // Mantemos somente a janela recente em tempo real. Um documento extra permite
  // saber se existe histórico paginável sem abrir listener sobre toda a coleção.
  const centralQuery = query(
    centralRef,
    orderBy('createdAt', 'desc'),
    limit(CENTRAL_LIVE_PAGE_SIZE + 1),
  );
  const unsubscribeCentral = onSnapshot(centralQuery, (snapshot) => {
    const visibleDocs = snapshot.docs.slice(0, CENTRAL_LIVE_PAGE_SIZE);
    onChange(visibleDocs.map(normalizeCentralItem), {
      cursor: visibleDocs.length ? visibleDocs[visibleDocs.length - 1] : null,
      hasMore: snapshot.docs.length > CENTRAL_LIVE_PAGE_SIZE,
    });
  }, onError);

  return () => {
    unsubscribeCentral();
  };
};

export const listenServidorUnreadNotificationFlag = ({ uid, onChange, onError }) => {
  const safeUid = String(uid || '').trim();
  if (!safeUid) {
    onChange?.(false);
    return () => {};
  }

  const centralRef = collection(db, `artifacts/${firestorePathId}/users/${safeUid}/notificationCenter`);
  const unreadQuery = query(centralRef, where('read', '==', false), limit(1));
  return onSnapshot(unreadQuery, (snapshot) => {
    onChange?.(!snapshot.empty);
  }, onError);
};

export const loadMoreServidorNotificationCenter = async ({
  uid,
  afterSnapshot,
  pageSize = CENTRAL_HISTORY_PAGE_SIZE,
}) => {
  const safeUid = String(uid || '').trim();
  const safePageSize = Math.max(1, Math.min(Number(pageSize) || CENTRAL_HISTORY_PAGE_SIZE, 100));
  if (!safeUid || !afterSnapshot) {
    return {items: [], cursor: afterSnapshot || null, hasMore: false};
  }

  const centralRef = collection(db, `artifacts/${firestorePathId}/users/${safeUid}/notificationCenter`);
  const historyQuery = query(
    centralRef,
    orderBy('createdAt', 'desc'),
    // Incluímos o cursor anterior na página e deduplicamos no contexto. Isso
    // evita lacuna se novos eventos deslocarem a janela ao vivo durante a leitura.
    startAt(afterSnapshot),
    limit(safePageSize + 2),
  );
  const snapshot = await getDocs(historyQuery);
  const visibleDocs = snapshot.docs.slice(0, safePageSize + 1);
  return {
    items: visibleDocs.map(normalizeCentralItem),
    cursor: visibleDocs.length ? visibleDocs[visibleDocs.length - 1] : afterSnapshot,
    hasMore: snapshot.docs.length > safePageSize + 1,
  };
};

export const loadLegacyUnreadNotifications = async ({ municipioId, uid, cpf }) => {
  const safeMunicipioId = String(municipioId || '').trim();
  const safeUid = String(uid || '').trim();
  const safeCpf = String(cpf || '').replace(/\D/g, '').slice(0, 11);
  const recipientIds = [...new Set([safeUid, safeCpf].filter(Boolean))];
  if (!safeMunicipioId || recipientIds.length === 0) return [];

  const notificationsRef = collection(
    db,
    `artifacts/${firestorePathId}/public/data/municipalities/${safeMunicipioId}/notifications`,
  );

  // Compatibilidade somente para o histórico pré-Central. Novos documentos são
  // adaptados pelo backend para notificationCenter, portanto não precisamos
  // manter um listener ilimitado sobre toda a coleção antiga.
  const legacyQueries = recipientIds.flatMap((recipientId) => ([
    query(
      notificationsRef,
      where('recipientId', '==', recipientId),
      where('read', '==', false),
      limit(100),
    ),
    query(
      notificationsRef,
      where('recipientId', '==', recipientId),
      where('lida', '==', false),
      limit(100),
    ),
  ]));

  const snapshots = await Promise.all(legacyQueries.map((legacyQuery) => getDocs(legacyQuery)));
  const byId = new Map();
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((docSnapshot) => {
      const data = docSnapshot.data() || {};
      const readBy = Array.isArray(data.readBy) ? data.readBy.map(String) : [];
      if (safeUid && readBy.includes(safeUid)) return;
      const item = normalizeLegacyItem(docSnapshot);
      if (item.read !== true) byId.set(item.id, item);
    });
  });

  return [...byId.values()].sort((a, b) => b.createdAtMs - a.createdAtMs);
};

const chunkValues = (values, size = 30) => {
  const chunks = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
};

export const listenNotificationReadReceipts = ({uid, entityIds = [], onChange, onError}) => {
  const safeUid = String(uid || '').trim();
  const safeEntityIds = [...new Set(
    entityIds.map((value) => String(value || '').trim()).filter(Boolean),
  )].slice(0, 100);
  if (!safeUid || safeEntityIds.length === 0) {
    onChange?.(new Map());
    return () => {};
  }

  const receiptsRef = collection(
    db,
    `artifacts/${firestorePathId}/users/${safeUid}/notificationReadReceipts`,
  );
  const snapshotsByChunk = new Map();
  const receiptIds = safeEntityIds.map((entityId) => `aviso_${entityId}`);
  const emit = () => {
    const receipts = new Map();
    snapshotsByChunk.forEach((snapshot) => {
      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data() || {};
        if (data.read === true && data.eventType === 'aviso') {
          receipts.set(String(data.entityId || ''), true);
        }
      });
    });
    onChange?.(receipts);
  };

  const unsubscribers = chunkValues(receiptIds, 30).map((chunk, index) => onSnapshot(
    query(receiptsRef, where(documentId(), 'in', chunk)),
    (snapshot) => {
      snapshotsByChunk.set(index, snapshot);
      emit();
    },
    onError,
  ));

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
};

export const mergeServidorNotifications = mergeNotifications;

export const setServidorNotificationRead = async ({ uid, municipioId, item, read = true }) => {
  const safeUid = String(uid || '').trim();
  if (!safeUid || !item?.id) throw new Error('Notificação inválida.');

  if (item.storage === 'notificationCenter') {
    const ref = doc(db, `artifacts/${firestorePathId}/users/${safeUid}/notificationCenter/${item.id}`);
    await updateDoc(ref, {
      read: Boolean(read),
      readAt: read ? serverTimestamp() : null,
    });
    return;
  }

  if (item.storage === 'aviso') {
    const safeMunicipioId = String(municipioId || '').trim();
    const ref = doc(
      db,
      `artifacts/${firestorePathId}/users/${safeUid}/notificationReadReceipts/aviso_${item.id}`,
    );
    if (read) {
      await setDoc(ref, {
        eventType: 'aviso',
        entityId: String(item.id),
        municipioId: safeMunicipioId,
        read: true,
        readAt: serverTimestamp(),
      }, {merge: false});
    }
    return;
  }

  if (item.storage === 'legacyNotification') {
    const safeMunicipioId = String(municipioId || '').trim();
    if (!safeMunicipioId) throw new Error('Município inválido para atualizar a notificação.');
    const ref = doc(
      db,
      `artifacts/${firestorePathId}/public/data/municipalities/${safeMunicipioId}/notifications/${item.id}`,
    );
    await updateDoc(ref, {readBy: arrayUnion(safeUid)});
  }
};
